import { useCallback, useEffect, useState } from "react";
import {
  ArrayField,
  Datagrid,
  FunctionField,
  NumberField,
  RecordContextProvider,
  ReferenceField,
  TextField,
  useRecordContext,
} from "react-admin";
import { Box, Chip, CircularProgress } from "@mui/material";
import dataProvider from "server/dataProvider";
import { doctoralRequests } from "api/doctoralApi";
import { settings } from "app";
import { administrationRequests } from "api/administrationApi";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import LanguageCommitteeDialog from "./LanguageCommitteeDialog";
import LanguageScheduleDialog from "./LanguageScheduleDialog";
import { useNotifyError } from "utils/useNotifyError";
import DoctoralDateField from "../../Partials/DoctoralDateField";

const committeeChip = exam => (
  <Chip
    size="small"
    color={exam.hasCommittee ? "success" : "default"}
    label={exam.hasCommittee ? "Избрана" : "—"}
  />
);

const LanguageExamsSection = () => {
  const notifyError = useNotifyError();
  const campaign = useRecordContext();
  const [loading, setLoading] = useState(true);
  const [exams, setExams] = useState([]);

  const campaignId = campaign?.id;

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const { data } = await dataProvider.customRequest(doctoralRequests.getLanguageExams(campaignId), null, "GET");
      setExams((Array.isArray(data) ? data : []).map(x => ({ ...x, id: x.languageId })));
    } catch (err) {
      notifyError(err);
    } finally {
      setLoading(false);
    }
  }, [campaignId, notifyError]);

  useEffect(() => {
    load();
  }, [load]);

  if (!campaign) return null;

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress />
      </Box>
    );

  return (
    <RecordContextProvider value={{ id: campaignId, languageExams: exams }}>
      <ArrayField source="languageExams">
        <Datagrid
          bulkActionButtons={false}
          rowClick={false}
          sx={settings.style.secondaryDatagrid}
          empty={<em>Няма избрани езици от кандидатите</em>}
        >
          <TextField
            source="languageNameBg"
            label="Език"
            sortable={false}
          />
          <NumberField
            source="candidateCount"
            label="Допуснати до изпит по език"
            sortable={false}
          />
          <DoctoralDateField
            source="examDate"
            label="Дата на изпита"
            showTime
            sortable={false}
            emptyText="—"
          />
          <ReferenceField
            label="Зала"
            source="examRoomId"
            reference={administrationRequests.getRoomsSelect()}
            link={false}
            sortable={false}
            emptyText="—"
          >
            <FunctionField render={r => (r ? `${r.roomName} - ${r.buildingName}` : "—")} />
          </ReferenceField>
          <FunctionField
            label="Комисия"
            render={committeeChip}
          />
          <FunctionField
            label=""
            render={exam => (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                <SecuredComponent permission={permissions.canManageDoctoralLanguageExams}>
                  <LanguageCommitteeDialog
                    campaignId={campaignId}
                    languageId={exam.languageId}
                    exam={exam}
                    onSaved={load}
                  />
                </SecuredComponent>
                {exam.hasCommittee ? (
                  <SecuredComponent permission={permissions.canManageDoctoralLanguageExams}>
                    <LanguageScheduleDialog
                      campaignId={campaignId}
                      languageId={exam.languageId}
                      exam={exam}
                      onSaved={load}
                    />
                  </SecuredComponent>
                ) : (
                  <span style={{ fontSize: 12, color: "#888" }}>Първо изберете изпитна комисия</span>
                )}
              </Box>
            )}
          />
        </Datagrid>
      </ArrayField>
    </RecordContextProvider>
  );
};

export default LanguageExamsSection;
